import { Injectable, effect, inject } from '@angular/core';
import { faro } from '@grafana/faro-web-sdk';
import { AuthService } from '@app/core/services/auth.service';

/**
 * Mirrors the in-memory auth session into Faro's user context so errors, logs
 * and sessions can be traced to an account WITHOUT shipping PII. Only the
 * stable backend user id and non-identifying categorical fields (role,
 * userType, accountType) are sent — never email, userName or any other
 * personal data (AGENTS.md "No PII in logs or URLs").
 *
 * Constructed eagerly at startup via `provideAppInitializer`, so the effect is
 * live before any signal fires. It reacts to every `currentUser` transition:
 * login / 2FA / register / session-restore set the user; logout and the 401
 * session-clear reset it.
 */
@Injectable({ providedIn: 'root' })
export class FaroUserTracker {
  private readonly auth = inject(AuthService);

  constructor() {
    effect(() => {
      const user = this.auth.currentUser();
      if (user) {
        faro.api?.setUser({
          id: user.id,
          roles: user.role,
          attributes: {
            userType: user.userType,
            accountType: user.accountType ?? 'unknown',
          },
        });
      } else {
        faro.api?.resetUser();
      }
    });
  }
}
